// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * WishesService
 * Handles read/write for: medical_wishes, funeral_wishes, wishes (general),
 * document_index, messages (envelopes), personal_farewell.
 *
 * MVP approach: data stored as JSON in encryptedContent field.
 * When masterKey is available → AES-256-GCM encryption.
 * When not available → plain JSON stored as ArrayBuffer (offline demo mode).
 */
import { db }          from '@/lib/db'
import { getMasterKey, saveModuleProgress } from '@/services/profile'
import { generateUUID, nowISO }             from '@/lib/utils'
import { SyncStatus, CompletionStatus }     from '@/types'

// ── Crypto helpers (mirrors profile service) ──────────────────────────────────

async function encrypt(data: object, key: CryptoKey): Promise<{ blob: ArrayBuffer; iv: string }> {
  const iv      = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(JSON.stringify(data))
  const blob    = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded)
  return { blob, iv: btoa(String.fromCharCode(...iv)) }
}

async function decrypt<T>(blob: ArrayBuffer, ivB64: string, key: CryptoKey): Promise<T> {
  const iv        = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0))
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, blob)
  return JSON.parse(new TextDecoder().decode(decrypted)) as T
}

/** Store object as ArrayBuffer (plain, no encryption) for fallback */
function toPlainBlob(data: object): ArrayBuffer {
  return new TextEncoder().encode(JSON.stringify(data)).buffer
}

function fromPlainBlob<T>(buf: ArrayBuffer): T {
  return JSON.parse(new TextDecoder().decode(buf)) as T
}

// ── Generic encode/decode (uses encryption when key available) ─────────────────

async function encodeContent(
  data:   object,
  userId: string,
): Promise<{ encryptedContent: ArrayBuffer; blobIv: string }> {
  const key = getMasterKey(userId)
  if (key) {
    const { blob, iv } = await encrypt(data, key)
    return { encryptedContent: blob, blobIv: iv }
  }
  return { encryptedContent: toPlainBlob(data), blobIv: '' }
}

async function decodeContent<T>(
  encryptedContent: ArrayBuffer | null,
  blobIv:           string | null,
  userId:           string,
): Promise<T | null> {
  if (!encryptedContent) return null
  // V41-fix: blobIv gevuld → data is versleuteld → key vereist
  // Nooit ciphertext als plain JSON parsen (JSON.parse op ciphertext → throw → null)
  if (blobIv) {
    const key = getMasterKey(userId)
    if (!key) return null   // key ontbreekt na reload → null, GEEN overschrijving
    try { return await decrypt<T>(encryptedContent, blobIv, key) }
    catch { return null }
  }
  // blobIv leeg → plain opgeslagen (geen key beschikbaar was bij opslaan)
  try { return fromPlainBlob<T>(encryptedContent) }
  catch { return null }
}

// ─────────────────────────────────────────────────────────────────────────────
// MEDICAL WISHES
// ─────────────────────────────────────────────────────────────────────────────

export interface MedicalWishesData {
  treatmentPreferences:  string
  resuscitation:         string
  organDonation:         string
  organDonationDetails:  string
  hospicePreference:     string
  gpName:                string
  gpContact:             string
  gpPractice:            string
  gpPhone:               string
  gpCity:                string
  livingWillExists:      string   // 'yes' | 'no' | 'pending' | '' - backward compat: true→'yes'
  livingWillLocation:    string
  livingWillNotaris:     string
  livingWillDate:        string
  proxySource:           string
  proxyDecisionMaker:    string
  proxyPhone:            string
  proxyEmail:            string
  // V41: reserve medisch vertegenwoordiger
  reserveProxySource:    string
  reserveProxyName:      string
  reserveProxyPhone:     string
  reserveProxyEmail:     string
  additionalWishes:      string
}
export const EMPTY_MEDICAL: MedicalWishesData = {
  treatmentPreferences:  '',
  resuscitation:         '',
  organDonation:         '',
  organDonationDetails:  '',
  hospicePreference:     '',
  gpName:                '',
  gpContact:             '',
  gpPractice:            '',
  gpPhone:               '',
  gpCity:                '',
  livingWillExists:      '',
  livingWillLocation:    '',
  livingWillNotaris:     '',
  livingWillDate:        '',
  proxySource:           '',
  proxyDecisionMaker:    '',
  proxyPhone:            '',
  proxyEmail:            '',
  reserveProxySource:    '',
  reserveProxyName:      '',
  reserveProxyPhone:     '',
  reserveProxyEmail:     '',
  additionalWishes:      '',
}

export async function loadMedicalWishes(userId: string): Promise<MedicalWishesData | null> {
  const rec = await db.medical_wishes.where('userId').equals(userId).first()
  if (!rec || !rec.encryptedContent) return null
  const data = await decodeContent<MedicalWishesData>(rec.encryptedContent, rec.blobIv, userId)
  if (!data) return null
  // Backward compat: livingWillExists was stored as boolean before V37-P1
  if (typeof (data.livingWillExists as unknown) === 'boolean') {
    data.livingWillExists = (data.livingWillExists as unknown as boolean) ? 'yes' : ''
  }
  return data
}

export async function saveMedicalWishes(userId: string, data: MedicalWishesData): Promise<void> {
  const { encryptedContent, blobIv } = await encodeContent(data, userId)

  // Calculate completion
  const filledFields = [
    data.treatmentPreferences,
    data.resuscitation,
    data.organDonation,
    data.gpName,
  ].filter(Boolean).length
  const pct = Math.round((filledFields / 4) * 100)
  const status = pct === 0 ? CompletionStatus.NOT_STARTED
    : pct === 100 ? CompletionStatus.COMPLETE
    : CompletionStatus.IN_PROGRESS

  const existing = await db.medical_wishes.where('userId').equals(userId).first()
  if (existing) {
    await db.medical_wishes.update(existing.userId, {
      encryptedContent,
      blobIv,
      hasLivingWill:             data.livingWillExists === 'yes',
      hasResuscitationDirective: data.resuscitation === 'no',
      completionStatus:          status,
      lastUpdatedAt:             nowISO(),
      syncStatus:                SyncStatus.PENDING,
    })
  } else {
    await db.medical_wishes.add({
      id:                         generateUUID(),
      userId,
      completionStatus:           status,
      hasLivingWill:              data.livingWillExists === 'yes',
      hasResuscitationDirective:  data.resuscitation === 'no',
      lastUpdatedAt:              nowISO(),
      createdAt:                  nowISO(),
      syncStatus:                 SyncStatus.PENDING,
      cloudSyncId:                null,
      encryptedContent,
      blobIv,
    })
  }

  await saveModuleProgress(userId, 'medical', filledFields, 4)
}

// ─────────────────────────────────────────────────────────────────────────────
// FUNERAL WISHES
// ─────────────────────────────────────────────────────────────────────────────

export interface FuneralWishesData {
  type:              string   // 'burial' | 'cremation' | 'natural' | ''
  // Location
  locationCity:      string   // woonplaats voor locatiezoekopdracht
  locationVenueType: string   // 'begraafplaats' | 'crematorium' | ''
  locationVenue:     string   // gekozen of handmatig ingevoerde locatienaam
  locationWish:      string   // legacy + buitenland/vrij tekstveld
  // Ceremony
  ceremonyType:      string   // 'religious' | 'civil' | 'private' | 'none' | 'other' | ''
  ceremonyTypeOther: string   // N2: vrij tekstveld bij 'anders'
  ceremonyWishes:    string
  // Funeral home
  funeralHome:       string   // gekozen of handmatig ingevoerd
  funeralHomeCity:   string   // woonplaats voor ondernemer-zoekopdracht
  funeralHomeStreet:      string   // straat
  funeralHomeHouseNumber: string   // huisnummer
  funeralHomePostcode:    string   // postcode
  funeralHomeEmail:       string   // e-mailadres uitvaartverzorger
  funeralHomeWebsite:     string   // website uitvaartverzorger
  funeralHomeContact:string   // naam contactpersoon bij uitvaartverzorger
  funeralHomePhone:  string   // telefoonnummer uitvaartverzorger
  funeralHomeNotes:  string   // persoonlijke toelichting ("zij kent onze familie")
  // Music N3
  musicSource:       string   // 'spotify' | 'youtube' | 'apple' | 'manual' | ''
  musicUrl:          string   // playlist URL
  music:             string   // handmatige beschrijving
  // Other
  flowers:           string
  clothing:          string
  obituaryText:      string
  notificationList:  string
  specialRequests:   string
  // N5 Rouwadvertentie
  obituaryAd:        string   // 'yes' | 'no' | ''
  obituaryAdWhere:   string   // 'regional' | 'national' | 'other' | ''
  obituaryAdText:    string
}

export const EMPTY_FUNERAL: FuneralWishesData = {
  type: '', locationCity: '', locationVenueType: '', locationVenue: '', locationWish: '',
  ceremonyType: '', ceremonyTypeOther: '', ceremonyWishes: '',
  funeralHome: '', funeralHomeCity: '', funeralHomeStreet: '', funeralHomeHouseNumber: '', funeralHomePostcode: '', funeralHomeEmail: '', funeralHomeWebsite: '', funeralHomeContact: '', funeralHomePhone: '', funeralHomeNotes: '',
  musicSource: '', musicUrl: '', music: '',
  flowers: '', clothing: '',
  obituaryText: '', notificationList: '', specialRequests: '',
  obituaryAd: '', obituaryAdWhere: '', obituaryAdText: '',
}

export async function loadFuneralWishes(userId: string): Promise<FuneralWishesData | null> {
  try {
    const rec = await db.funeral_wishes.where('userId').equals(userId).first()
    if (!rec || !rec.encryptedContent) {
      return null
    }
    const decoded = await decodeContent<FuneralWishesData>(rec.encryptedContent, rec.blobIv, userId)
    if (!decoded) {
      return null
    }
    return decoded
  } catch (err) {
    return null
  }
}

export async function saveFuneralWishes(userId: string, data: FuneralWishesData): Promise<void> {
  // V41: voorkom overschrijving van versleutelde data zonder key
  if (getMasterKey(userId) === null) {
    const existing = await db.funeral_wishes.where('userId').equals(userId).first()
    if (existing?.blobIv) return   // versleuteld record aanwezig, key weg → afbreken
  }
  try {
    const { encryptedContent, blobIv } = await encodeContent(data, userId)

    const filledFields = [data.type, data.ceremonyType, data.locationWish, data.music, data.clothing]
      .filter(Boolean).length
    const pct = Math.round((filledFields / 5) * 100)
    const status = pct === 0 ? CompletionStatus.NOT_STARTED
      : pct === 100 ? CompletionStatus.COMPLETE
      : CompletionStatus.IN_PROGRESS

    const existing = await db.funeral_wishes.where('userId').equals(userId).first()
    if (existing) {
      await db.funeral_wishes.update(existing.userId, {
        encryptedContent, blobIv,
        completionStatus: status,
        lastUpdatedAt:    nowISO(),
        syncStatus:       SyncStatus.PENDING,
      })
    } else {
      await db.funeral_wishes.add({
        id:               generateUUID(),
        userId,
        completionStatus: status,
        lastUpdatedAt:    nowISO(),
        createdAt:        nowISO(),
        syncStatus:       SyncStatus.PENDING,
        cloudSyncId:      null,
        encryptedContent,
        blobIv,
      })
    }

    await saveModuleProgress(userId, 'funeral', filledFields, 5)
  } catch (err) {
    throw err
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// GENERAL WISHES (wenseninventarisatie)
// ─────────────────────────────────────────────────────────────────────────────

export interface GeneralWishesData {
  lifeValues:       string
  importantPeople:  string
  importantPlaces:  string
  hobbiesActivities:string
  lastWishes:       string
  messageToWorld:   string
  digitalLegacy:    string
  financialNotes:   string
}

export const EMPTY_GENERAL: GeneralWishesData = {
  lifeValues:        '',
  importantPeople:   '',
  importantPlaces:   '',
  hobbiesActivities: '',
  lastWishes:        '',
  messageToWorld:    '',
  digitalLegacy:     '',
  financialNotes:    '',
}

export async function loadGeneralWishes(userId: string): Promise<GeneralWishesData | null> {
  const rec = await db.wishes.where('userId').equals(userId).first()
  if (!rec || !rec.encryptedContent) return null
  return decodeContent<GeneralWishesData>(rec.encryptedContent, rec.blobIv, userId)
}

export async function saveGeneralWishes(userId: string, data: GeneralWishesData): Promise<void> {
  const { encryptedContent, blobIv } = await encodeContent(data, userId)

  const filledFields = Object.values(data).filter((v) => typeof v === 'string' && v.trim()).length
  const total = Object.keys(data).length
  const status = filledFields === 0 ? CompletionStatus.NOT_STARTED
    : filledFields >= total ? CompletionStatus.COMPLETE
    : CompletionStatus.IN_PROGRESS

  const existing = await db.wishes.where('userId').equals(userId).first()
  if (existing) {
    await db.wishes.update(existing.id, {
      encryptedContent, blobIv,
      completionStatus: status,
      lastUpdatedAt:    nowISO(),
      syncStatus:       SyncStatus.PENDING,
    })
  } else {
    const { WishCategory } = await import('@/types')
    await db.wishes.add({
      id:               generateUUID(),
      userId,
      category:         WishCategory.PERSONAL,
      sortOrder:        0,
      completionStatus: status,
      lastUpdatedAt:    nowISO(),
      createdAt:        nowISO(),
      syncStatus:       SyncStatus.PENDING,
      cloudSyncId:      null,
      encryptedContent,
      blobIv,
    })
  }

  await saveModuleProgress(userId, 'wishes', filledFields, total)
}

// ─────────────────────────────────────────────────────────────────────────────
// DOCUMENT INDEX (Waar vind ik wat?)
// ─────────────────────────────────────────────────────────────────────────────

export interface DocumentEntry {
  id:          string
  category:    string
  label:       string
  location:    string
  notes:       string
  isVerified:  boolean
}

export const DOCUMENT_CATEGORIES = [
  { value: 'IDENTITY',    label: 'Identiteitsdocumenten' },
  { value: 'FINANCIAL',   label: 'Financieel' },
  { value: 'INSURANCE',   label: 'Verzekeringen' },
  { value: 'PROPERTY',    label: 'Woning & Eigendom' },
  { value: 'LEGAL',       label: 'Juridisch' },
  { value: 'MEDICAL',     label: 'Medisch' },
  { value: 'OTHER',       label: 'Overig' },
]

export async function loadDocuments(userId: string): Promise<DocumentEntry[]> {
  const records = await db.document_index.where('userId').equals(userId).toArray()
  const entries: DocumentEntry[] = []
  for (const rec of records) {
    const content = await decodeContent<{ location: string; notes: string }>(
      rec.encryptedContent, rec.blobIv, userId,
    )
    entries.push({
      id:         rec.id,
      category:   rec.documentCategory,
      label:      rec.documentLabel,
      location:   content?.location ?? '',
      notes:      content?.notes    ?? '',
      isVerified: rec.isVerified,
    })
  }
  return entries
}

export async function saveDocument(userId: string, entry: Omit<DocumentEntry, 'id'>): Promise<string> {
  const { encryptedContent, blobIv } = await encodeContent(
    { location: entry.location, notes: entry.notes }, userId,
  )
  const id = generateUUID()
  const { DocumentCategory } = await import('@/types')
  await db.document_index.add({
    id,
    userId,
    documentCategory: (entry.category as typeof DocumentCategory[keyof typeof DocumentCategory]),
    documentLabel:    entry.label,
    sortOrder:        0,
    isVerified:       entry.isVerified,
    lastUpdatedAt:    nowISO(),
    createdAt:        nowISO(),
    syncStatus:       SyncStatus.PENDING,
    cloudSyncId:      null,
    encryptedContent,
    blobIv,
  })
  // Tel alle documenten na opslaan
  const totalDocs = await db.document_index.where('userId').equals(userId).count()
  await saveModuleProgress(userId, 'documents', totalDocs, 3)
  return id
}

export async function updateDocument(
  userId: string,
  entryId: string,
  entry: Omit<DocumentEntry, 'id'>,
): Promise<void> {
  const { encryptedContent, blobIv } = await encodeContent(
    { location: entry.location, notes: entry.notes }, userId,
  )
  const { DocumentCategory } = await import('@/types')
  await db.document_index.update(entryId, {
    documentCategory: (entry.category as typeof DocumentCategory[keyof typeof DocumentCategory]),
    documentLabel:    entry.label,
    isVerified:       entry.isVerified,
    encryptedContent,
    blobIv,
    lastUpdatedAt:    nowISO(),
    syncStatus:       SyncStatus.PENDING,
  })
}

export async function deleteDocument(entryId: string): Promise<void> {
  await db.document_index.delete(entryId)
}

// ─────────────────────────────────────────────────────────────────────────────
// MESSAGES (Berichtenservice)
// ─────────────────────────────────────────────────────────────────────────────

export interface MessageDraft {
  id:             string
  recipientLabel: string
  content:        string
  status:         'DRAFT' | 'READY' | 'RELEASED'
  createdAt:      string
  updatedAt:      string
  // V34.7: kanaaldata opgeslagen in encryptedContent
  recipientPhone: string
  recipientEmail: string
  relation:       string
  sendMethod:     string   // 'whatsapp' | 'sms' | 'email'
  sendMoment:     string
  customMoment:   string
}

export async function loadMessages(userId: string): Promise<MessageDraft[]> {
  const records = await db.messages.where('userId').equals(userId).toArray()
  const drafts: MessageDraft[] = []
  for (const rec of records) {
    if (rec.withdrawnAt) continue
    // V34.7: encryptedContent bevat { text, recipientPhone, recipientEmail, relation, sendMethod, sendMoment, customMoment }
    // Backward compatible: oude records hebben alleen { text }
    const decoded = await decodeContent<{
      text: string
      recipientPhone?: string; recipientEmail?: string; relation?: string
      sendMethod?: string; sendMoment?: string; customMoment?: string
    }>(rec.encryptedContent, rec.blobIv, userId)
    drafts.push({
      id:             rec.id,
      recipientLabel: rec.recipientLabel ?? '',
      content:        decoded?.text ?? '',
      status:         rec.draftStatus as 'DRAFT' | 'READY' | 'RELEASED',
      createdAt:      rec.createdAt,
      updatedAt:      rec.updatedAt,
      recipientPhone: decoded?.recipientPhone ?? '',
      recipientEmail: decoded?.recipientEmail ?? '',
      relation:       decoded?.relation ?? '',
      sendMethod:     decoded?.sendMethod ?? 'whatsapp',
      sendMoment:     decoded?.sendMoment ?? 'direct',
      customMoment:   decoded?.customMoment ?? '',
    })
  }
  return drafts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

export async function saveMessage(
  userId:  string,
  draft:   {
    recipientLabel: string; content: string; status: 'DRAFT' | 'READY'
    // V34.7: optionele kanaalvelden
    recipientPhone?: string; recipientEmail?: string; relation?: string
    sendMethod?: string; sendMoment?: string; customMoment?: string
  },
  id?:     string,
): Promise<string> {
  // Sla alle kanaalvelden op in de versleutelde blob
  const { encryptedContent, blobIv } = await encodeContent({
    text:           draft.content,
    recipientPhone: draft.recipientPhone ?? '',
    recipientEmail: draft.recipientEmail ?? '',
    relation:       draft.relation ?? '',
    sendMethod:     draft.sendMethod ?? 'whatsapp',
    sendMoment:     draft.sendMoment ?? 'direct',
    customMoment:   draft.customMoment ?? '',
  }, userId)
  const preview = draft.content.slice(0, 50)
  const { MessageDraftStatus } = await import('@/types')

  if (id) {
    await db.messages.update(id, {
      recipientLabel:  draft.recipientLabel,
      encryptedContent,
      blobIv,
      contentPreview:  preview,
      draftStatus:     draft.status as typeof MessageDraftStatus[keyof typeof MessageDraftStatus],
      updatedAt:       nowISO(),
      syncStatus:      SyncStatus.PENDING,
    })
    return id
  }

  const newId = generateUUID()
  await db.messages.add({
    id:               newId,
    userId,
    createdAt:        nowISO(),
    updatedAt:        nowISO(),
    recipientLabel:   draft.recipientLabel,
    encryptedContent,
    blobIv,
    contentPreview:   preview,
    draftStatus:      draft.status as typeof MessageDraftStatus[keyof typeof MessageDraftStatus],
    releasedAt:       null,
    firstOpenedAt:    null,
    openedBySession:  null,
    withdrawnAt:      null,
    withdrawnReason:  null,
    syncStatus:       SyncStatus.PENDING,
    cloudSyncId:      null,
  })
  // Tel alle actieve berichten na opslaan
  const totalMessages = await db.messages.where('userId').equals(userId)
    .filter(m => !m.withdrawnAt).count()
  await saveModuleProgress(userId, 'messages', totalMessages, 2)
  return newId
}

export async function deleteMessage(id: string): Promise<void> {
  await db.messages.delete(id)
}

// ─────────────────────────────────────────────────────────────────────────────
// PERSONAL FAREWELL
// ─────────────────────────────────────────────────────────────────────────────

export interface FarewellMessageMeta {
  share?: string[]   // 'Later delen'-voorkeuren: 'pdf'|'inperson'|'wilsapp'|'service' (alleen voorkeuren, geen verzendactie)
}
export interface FarewellMeta {
  messages?: Record<string, FarewellMessageMeta>  // per kaart-id
}

export interface PersonalFarewellData {
  generalMessage: string
  letterPartner:  string
  letterChildren: string
  letterFamily:   string
  letterFriends:  string
  practicalNotes: string
  // V49: lichte 'Later delen'-voorkeuren per brief (geen tweede berichtenmodel; recipient/verzending/vrijgave blijven in de Berichtenservice)
  meta?: FarewellMeta
}

export const EMPTY_FAREWELL: PersonalFarewellData = {
  generalMessage: '',
  letterPartner:  '',
  letterChildren: '',
  letterFamily:   '',
  letterFriends:  '',
  practicalNotes: '',
}

export async function loadPersonalFarewell(userId: string): Promise<PersonalFarewellData | null> {
  const rec = await db.personal_farewell.where('userId').equals(userId).first()
  if (!rec || !rec.encryptedContent) return null
  return decodeContent<PersonalFarewellData>(rec.encryptedContent, rec.blobIv, userId)
}

export async function savePersonalFarewell(userId: string, data: PersonalFarewellData): Promise<void> {
  const { encryptedContent, blobIv } = await encodeContent(data, userId)
  const strVals = Object.values(data).filter(v => typeof v === 'string') as string[]
  const filled  = strVals.filter(v => v.trim()).length
  const total   = strVals.length
  const status = filled === 0 ? CompletionStatus.NOT_STARTED
    : filled >= total ? CompletionStatus.COMPLETE
    : CompletionStatus.IN_PROGRESS

  const existing = await db.personal_farewell.where('userId').equals(userId).first()
  if (existing) {
    await db.personal_farewell.update(existing.userId, {
      encryptedContent, blobIv,
      completionStatus: status,
      hasMainLetter:    !!data.generalMessage,
      recipientCount:   strVals.filter(v => v.trim()).length,
      lastUpdatedAt:    nowISO(),
      syncStatus:       SyncStatus.PENDING,
    })
  } else {
    await db.personal_farewell.add({
      id:               generateUUID(),
      userId,
      hasMainLetter:    !!data.generalMessage,
      recipientCount:   filled,
      completionStatus: status,
      lastUpdatedAt:    nowISO(),
      createdAt:        nowISO(),
      syncStatus:       SyncStatus.PENDING,
      cloudSyncId:      null,
      encryptedContent,
      blobIv,
    })
  }
  await saveModuleProgress(userId, 'farewell', filled, total)
}

// ─────────────────────────────────────────────────────────────────────────────
// MUNICIPALITY (Gemeente & Overheid)
// ─────────────────────────────────────────────────────────────────────────────

export interface MunicipalityData {
  // Contact & locaal
  municipalityName:     string
  municipalityPhone:    string
  municipalityWebsite:  string
  // Praktisch
  bankName:             string
  insuranceNotes:       string
  pensionFundName:      string
  employerName:         string
  subscriptionsNotes:   string
  wmoEquipment:         string
  parkingPermit:        string
  // Notities
  personalNotes:        string
}

export const EMPTY_MUNICIPALITY: MunicipalityData = {
  municipalityName:    '',
  municipalityPhone:   '',
  municipalityWebsite: '',
  bankName:            '',
  insuranceNotes:      '',
  pensionFundName:     '',
  employerName:        '',
  subscriptionsNotes:  '',
  wmoEquipment:        '',
  parkingPermit:       '',
  personalNotes:       '',
}

export async function loadMunicipality(userId: string): Promise<MunicipalityData | null> {
  const rec = await db.wishes
    .where('[userId+category]').equals([userId, 'OTHER'])
    .filter(r => (r as unknown as {moduleKey?: string}).moduleKey === 'municipality')
    .first()
  if (!rec || !rec.encryptedContent) return null
  return decodeContent<MunicipalityData>(rec.encryptedContent, rec.blobIv, userId)
}

export async function saveMunicipality(userId: string, data: MunicipalityData): Promise<void> {
  const { WishCategory } = await import('@/types')
  const { encryptedContent, blobIv } = await encodeContent(data, userId)
  const filled = Object.values(data).filter(v => v.trim()).length

  const mStatus = filled === 0 ? CompletionStatus.NOT_STARTED : CompletionStatus.IN_PROGRESS
  const existing = await db.wishes
    .where('[userId+category]').equals([userId, 'OTHER'])
    .filter(r => (r as unknown as {moduleKey?: string}).moduleKey === 'municipality')
    .first()

  if (existing) {
    await db.wishes.update(existing.id, {
      encryptedContent, blobIv,
      completionStatus: mStatus,
      lastUpdatedAt: nowISO(), syncStatus: SyncStatus.PENDING,
    })
  } else {
    await db.wishes.add({
      id: generateUUID(), userId,
      category: WishCategory.OTHER,
      sortOrder: 99,
      completionStatus: mStatus,
      lastUpdatedAt: nowISO(), createdAt: nowISO(),
      syncStatus: SyncStatus.PENDING, cloudSyncId: null,
      encryptedContent, blobIv,
    })
  }
  await saveModuleProgress(userId, 'municipality', filled, 4)
}

// ─────────────────────────────────────────────────────────────────────────────
// DIGITAL ESTATE
// ─────────────────────────────────────────────────────────────────────────────

export interface DigitalEstateData {
  // ── Telefoon (V41) ────────────────────────────────────────────────────────
  hasSmartphone:           string   // 'yes' | 'no' | ''
  phoneBrand:              string
  phoneModel:              string
  phoneNumber:             string
  phoneProvider:           string
  // ── Toegang (V41) ─────────────────────────────────────────────────────────
  unlockMethod:            string   // 'pincode' | 'pattern' | 'face' | 'fingerprint' | 'other' | ''
  unlockCode:              string   // ⚠️ gevoelig - gemaskeerd in UI
  unlockCodeLocation:      string   // V41-P2: LOCATIE van de code (niet de code zelf)
  computerAccessLocation:  string   // V41-P2: Waar te vinden: laptop/computer-toegang
  // ── Authenticator (V41) ───────────────────────────────────────────────────
  authenticatorApp:        string   // 'microsoft' | 'google' | 'duo' | 'authy' | 'other' | 'none'
  authenticatorNotes:      string
  // ── Wachtwoordmanager (V41) ───────────────────────────────────────────────
  passwordManagerApp:      string   // '1password' | 'bitwarden' | 'lastpass' | 'apple' | 'google' | 'other' | 'none'
  passwordManagerLocation: string   // ⚠️ gevoelig
  // ── Digitale noodenvelop (V41) ────────────────────────────────────────────
  emergencyEnvelopeNotes:  string   // ⚠️ zeer gevoelig - gemaskeerd in UI
  // ── Bestaande velden (backward compat) ───────────────────────────────────
  emailAccounts:           string
  devicesNotes:            string
  socialMedia:             string
  cloudStorage:            string
  streamingServices:       string
  subscriptions:           string
  domainNames:             string
  websites:                string
  crypto:                  string
  investments:             string
  passwordManager:         string
  importantInstructions:   string
}

export const EMPTY_DIGITAL_ESTATE: DigitalEstateData = {
  // Telefoon
  hasSmartphone: '', phoneBrand: '', phoneModel: '', phoneNumber: '', phoneProvider: '',
  // Toegang
  unlockMethod: '', unlockCode: '', unlockCodeLocation: '', computerAccessLocation: '',
  // Authenticator
  authenticatorApp: '', authenticatorNotes: '',
  // Wachtwoordmanager
  passwordManagerApp: '', passwordManagerLocation: '',
  // Noodenvelop
  emergencyEnvelopeNotes: '',
  // Bestaand
  emailAccounts: '', devicesNotes: '', socialMedia: '', cloudStorage: '',
  streamingServices: '', subscriptions: '', domainNames: '', websites: '',
  crypto: '', investments: '', passwordManager: '', importantInstructions: '',
}

export async function loadDigitalEstate(userId: string): Promise<DigitalEstateData | null> {
  const rec = await db.digital_estate.where('userId').equals(userId).first()
  if (!rec || !rec.encryptedContent) return null
  return decodeContent<DigitalEstateData>(rec.encryptedContent, rec.blobIv, userId)
}

export async function saveDigitalEstate(userId: string, data: DigitalEstateData): Promise<void> {
  const { DigitalServiceCategory, DigitalDesiredAction } = await import('@/types')
  const { encryptedContent, blobIv } = await encodeContent(data, userId)
  const filled = Object.values(data).filter(v => v.trim()).length
  const existing = await db.digital_estate.where('userId').equals(userId).first()
  if (existing) {
    await db.digital_estate.update(existing.id, {
      encryptedContent, blobIv, lastUpdatedAt: nowISO(), syncStatus: SyncStatus.PENDING,
    })
  } else {
    await db.digital_estate.add({
      id: generateUUID(), userId,
      serviceCategory:  DigitalServiceCategory.OTHER,
      serviceLabel:     'Digitale nalatenschap',
      desiredAction:    DigitalDesiredAction.OTHER,
      sortOrder:        0,
      lastUpdatedAt:    nowISO(), createdAt: nowISO(),
      syncStatus:       SyncStatus.PENDING, cloudSyncId: null,
      encryptedContent, blobIv,
    })
  }
  await saveModuleProgress(userId, 'digital_estate', filled, 6)
}
